import { lazy, type ComponentType, type LazyExoticComponent } from "react"
import type { LucideIcon } from "lucide-react"

/** The plan task that builds a route; read by the coverage audit (Task 0.5). */
export type TaskId = `${number}.${number}`

export type NavGroup =
  "messaging" | "gateway" | "billing" | "administration" | "money" | "operations"

/** The sidebar's group order (PDD §6.1). */
export const NAV_GROUPS: readonly NavGroup[] = [
  "messaging",
  "gateway",
  "billing",
  "administration",
  "money",
  "operations",
]

/**
 * A route declares the permission it requires ALONGSIDE itself, so the guard,
 * the navigation and the coverage audit all read one source rather than three
 * that drift (LLD §7.1).
 */
export interface RouteDef {
  path: string
  element: LazyExoticComponent<ComponentType>
  /** Every listed permission is required (spec x-permission semantics). Omit for any signed-in user. */
  permission?: string | readonly string[]
  /** "any staff permission" (the admin overview). */
  staff?: boolean
  /** Account routes: allowed to staff without `permission` (DV-55). */
  staffExempt?: boolean
  /** Removed from the route table in standalone (renders NotFound). */
  money?: boolean
  /** Designed for < 768 px; otherwise `RequiresLargerScreen` there (LLD §7.2). */
  phoneSafe: boolean
  /** `nav:` key of the page's title, for routes without a menu entry (details, account). */
  titleKey?: string
  nav?: { group?: NavGroup; labelKey: string; icon: LucideIcon; order: number; shortcut?: string }
  owner: TaskId
}

/** Every screen a later task builds renders this until it lands (DW-87): a titled page, not 404. */
export const Planned = lazy(() => import("./placeholders/PlannedScreen"))

export const nav = (
  group: NavGroup | undefined,
  key: string,
  icon: LucideIcon,
  order: number,
  shortcut?: string,
): NonNullable<RouteDef["nav"]> => ({
  ...(group && { group }),
  labelKey: `entries.${key}`,
  icon,
  order,
  ...(shortcut !== undefined && { shortcut }),
})
